import React from 'react';
import { CustomCheckbox } from 'components/CustomCheckbox';
import { formatCurrency } from 'utils';
import '../../styles/PackSummary.scss';
import type {
  PackSummaryCard,
  PackSummaryFilter,
  PackSummaryProps,
} from '../../types';

const PackSummary: React.FC<PackSummaryProps> = ({
  summary,
  backstockSummaryOverride,
  isLoading,
  activeFilter = null,
  onFilterSelect,
  isWeeklySalesEnabled = false,
  onWeeklySalesToggle,
  isMissingEnabled = false,
  onMissingToggle,
}) => {
  const summaryCards: PackSummaryCard[] = [
    {
      label: 'Packs on Counter',
      amount: summary.activePacksOnCounter.amount,
      count: summary.activePacksOnCounter.count,
      tickets: summary.activePacksOnCounter.tickets,
      filterKey: 'counter',
    },
    {
      label: 'Active Packs in Backstock',
      amount: backstockSummaryOverride?.amount ?? summary.activePacksInBackstock.amount,
      count: backstockSummaryOverride?.count ?? summary.activePacksInBackstock.count,
      tickets: backstockSummaryOverride?.tickets ?? summary.activePacksInBackstock.tickets,
      filterKey: 'backstock',
    },
    {
      label: 'Not Activated Packs',
      count: summary.notActivatedPacks.count,
      tickets: summary.notActivatedPacks.tickets,
      filterKey: 'notActivated',
    },
    {
      label: 'Show Weekly Sales',
      amount: summary.weeklySales.amount,
      count: summary.weeklySales.count,
      tickets: summary.weeklySales.tickets,
      filterKey: 'weekly',
    },
  ];

  const formatCount = (value: number) => value.toLocaleString();
  const formatCountWhenNonZero = (value: number) =>
    value > 0 ? value.toLocaleString() : '-';
  const formatCurrencyWhenNonZero = (value?: number) =>
    typeof value === 'number' && value !== 0 ? formatCurrency(value) : '-';

  const handleFilterSelect = (filterKey: PackSummaryFilter) => {
    if (!onFilterSelect || isLoading) {
      return;
    }

    const nextFilter = activeFilter === filterKey ? null : filterKey;
    onFilterSelect(nextFilter);
  };

  const handleWeeklySalesToggle = (
    event: React.ChangeEvent<HTMLInputElement> | { target: { checked: boolean } }
  ) => {
    onWeeklySalesToggle?.(event.target.checked);
  };

  const handleMissingToggle = (
    event: React.ChangeEvent<HTMLInputElement> | { target: { checked: boolean } }
  ) => {
    onMissingToggle?.(event.target.checked);
  };

  if (isLoading) {
    return (
      <div className="pack-summary-container">
        <div className="pack-summary">
          {summaryCards.map(({ label }) => (
            <div className="pack-summary-card" key={label}>
              <div className="pack-summary-head">
                <span className="pack-summary-label">{label}</span>
                <div className="pack-summary-skeleton pack-summary-skeleton--currency" />
              </div>
              <div className="pack-summary-details">
                <div className="pack-summary-skeleton" />
                <div className="pack-summary-skeleton pack-summary-skeleton--small" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="pack-summary-container">
      <div className="pack-summary">
        {summaryCards.map(({ label, amount, count, tickets, filterKey }) => {
          const isInteractive = Boolean(filterKey);
          const isWeeklyCard = filterKey === 'weekly';
          const isBackstockCard = filterKey === 'backstock';
          const hideTicketsValue = isBackstockCard;
          const isFilterEnabled = isWeeklyCard ? isWeeklySalesEnabled : isInteractive;
          const isActive = isFilterEnabled && activeFilter === filterKey;
          const cardClassName = `pack-summary-card${isActive ? ' is-active' : ''}${isFilterEnabled ? ' is-filterable' : ''}`;

          return (
            <div
              key={label}
              role={isFilterEnabled ? 'button' : undefined}
              tabIndex={isFilterEnabled ? 0 : -1}
              aria-pressed={isActive}
              className={cardClassName}
              onClick={() => isFilterEnabled && filterKey && handleFilterSelect(filterKey)}
            >
              <div className="pack-summary-head">
                {isWeeklyCard ? (
                  <div
                    className="pack-summary-label pack-summary-label--with-checkbox"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <CustomCheckbox
                      checked={isWeeklySalesEnabled}
                      onChange={handleWeeklySalesToggle}
                    />
                    <span>{label}</span>
                  </div>
                ) : (
                  <span className="pack-summary-label">{label}</span>
                )}
                <div className="pack-summary-head-right">
                  {isActive && <span className="pack-summary-active-badge">Filter On</span>}
                  <span className="pack-summary-subtext">{formatCountWhenNonZero(count)}</span>
                </div>
              </div>
              <div className="pack-summary-details">
                {typeof amount === 'number' ? (
                  <span className="pack-summary-currency">
                    {formatCurrencyWhenNonZero(amount)}
                  </span>
                ) : (
                  <span className="pack-summary-currency pack-summary-currency--empty" />
                )}
                <div className="pack-summary-right">
                  {!hideTicketsValue && (
                    <span className="pack-summary-tickets-value">
                      {tickets > 0 && (
                        <>
                          {formatCount(tickets)}{' '}
                          <span className="pack-summary-tickets-value-unit">
                            {tickets <= 1 ? 'ticket' : 'tickets'}
                          </span>
                        </>
                      )}
                      {tickets <= 0 && <span className="pack-summary-tickets-value-unit">-</span>}
                    </span>
                  )}
                  {isBackstockCard && (
                    <div
                      className="pack-summary-toggle"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <CustomCheckbox
                        checked={isMissingEnabled}
                        onChange={handleMissingToggle}
                      />
                      <span className="pack-summary-toggle-hint">Show Missing</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PackSummary;
